import { useState, useCallback } from 'react';
import { Droplets, Check, Eye, ArrowRight, ArrowLeft, Minus, ListChecks, FlaskConical } from 'lucide-react';
import InfoBox from '../InfoBox';

interface Part1LeChatelierProps {
  onStepComplete?: (stepId: string) => void;
  currentStep?: number;
}

interface TubeState {
  color: string;
  intensity: number; // 0-100
  additions: string[];
}

// Steps definition
const steps = [
  { id: 1, title: 'Prepare Reference', short: 'Mix Fe³⁺ + SCN⁻ to establish equilibrium' },
  { id: 2, title: 'Predict Fe³⁺ Shift', short: 'Predict what happens when Fe³⁺ is added' },
  { id: 3, title: 'Add Fe³⁺', short: 'Add FeCl₃ to Test Tube 1' },
  { id: 4, title: 'Predict SCN⁻ Shift', short: 'Predict what happens when SCN⁻ is added' },
  { id: 5, title: 'Add SCN⁻', short: 'Add KSCN to Test Tube 2' },
  { id: 6, title: 'Predict NaOH Shift', short: 'Predict what happens when Fe³⁺ is removed' },
  { id: 7, title: 'Add NaOH', short: 'Add NaOH to Test Tube 3' },
  { id: 8, title: 'Compare All Tubes', short: 'Compare colours to reference' },
];

// Info content for steps
const stepInfoContent: Record<number, { title: string; content: string; chemicalEquation?: string }> = {
  1: {
    title: 'Establishing Equilibrium',
    content: 'The reaction between Fe³⁺ and SCN⁻ forms the blood-red FeSCN²⁺ complex. At equilibrium, all three species coexist with concentrations determined by Keq.'
  },
  2: {
    title: 'Adding Fe³⁺ (from 1 M FeCl₃)',
    content: 'Le Chatelier\'s principle predicts that adding drops of 1 M FeCl₃ will shift the equilibrium to the right, consuming the added Fe³⁺ and producing more FeSCN²⁺. The blood-red colour should intensify.',
    chemicalEquation: 'Fe³⁺(aq) + SCN⁻(aq) ⇌ FeSCN²⁺(aq)'
  },
  4: {
    title: 'Adding SCN⁻ (from 1 M KSCN)',
    content: 'Similarly, adding drops of 1 M KSCN shifts equilibrium to the right. Both reactants push the system toward more product formation, intensifying the blood-red colour.'
  },
  6: {
    title: 'Removing a Reactant',
    content: 'NaOH precipitates Fe³⁺ as Fe(OH)₃, removing it from solution. The system shifts left to replace the lost Fe³⁺, decreasing FeSCN²⁺ concentration.',
    chemicalEquation: 'Fe³⁺ + 3OH⁻ → Fe(OH)₃(s)'
  },
  8: {
    title: 'Le Chatelier Summary',
    content: 'A system at equilibrium responds to stress by shifting to partially counteract that stress. Adding reactant shifts toward products; removing reactant shifts toward reactants.'
  }
};

// Color calculation based on FeSCN2+ concentration
const calculateColor = (intensity: number): string => {
  // From pale yellow (low) to blood red (high)
  const r = Math.min(139 + intensity, 255);
  const g = Math.max(34 - intensity * 0.3, 0);
  const b = Math.max(34 - intensity * 0.3, 0);
  return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
};

export default function Part1LeChatelier({ onStepComplete, currentStep = 1 }: Part1LeChatelierProps) {
  // State for info panel
  const [showInfo, setShowInfo] = useState<number | null>(null);

  // State for test tubes
  const [referencePrepped, setReferencePrepped] = useState(false);
  const [tubes, setTubes] = useState<Record<string, TubeState>>({
    reference: { color: '#B22222', intensity: 50, additions: [] },
    test1: { color: '#B22222', intensity: 50, additions: [] },
    test2: { color: '#B22222', intensity: 50, additions: [] },
    test3: { color: '#B22222', intensity: 50, additions: [] }
  });

  const [predictions, setPredictions] = useState<Record<string, string | null>>({
    fe3: null,
    scn: null,
    naoh: null
  });

  const [observations, setObservations] = useState<string[]>([]);
  const [comparisonComplete, setComparisonComplete] = useState(false);

  // Derive step completion
  const stepDone = [
    referencePrepped,
    !!predictions.fe3,
    tubes.test1.additions.length > 0,
    !!predictions.scn,
    tubes.test2.additions.length > 0,
    !!predictions.naoh,
    tubes.test3.additions.length > 0,
    comparisonComplete,
  ];
  const completedCount = stepDone.filter(Boolean).length;
  const activeStep = stepDone.findIndex(d => !d) + 1 || steps.length;

  // Handlers
  const handlePrepareReference = useCallback(() => {
    setReferencePrepped(true);
    if (onStepComplete) onStepComplete('leChatelier-1');
  }, [onStepComplete]);

  const handlePredictFe3 = useCallback((prediction: string) => {
    setPredictions(prev => ({ ...prev, fe3: prediction }));
    if (onStepComplete) onStepComplete('leChatelier-2');
  }, [onStepComplete]);

  const handleAddFe3 = useCallback(() => {
    if (predictions.fe3) {
      setTubes(prev => ({
        ...prev,
        test1: {
          ...prev.test1,
          intensity: Math.min(prev.test1.intensity + 35, 95),
          color: calculateColor(Math.min(prev.test1.intensity + 35, 95)),
          additions: [...prev.test1.additions, 'Fe³⁺']
        }
      }));
      setObservations(prev => [...prev, 'Tube 1: Color darkened (more blood red) after adding Fe³⁺']);
      if (onStepComplete) onStepComplete('leChatelier-3');
    }
  }, [predictions.fe3, onStepComplete]);

  const handlePredictSCN = useCallback((prediction: string) => {
    setPredictions(prev => ({ ...prev, scn: prediction }));
    if (onStepComplete) onStepComplete('leChatelier-4');
  }, [onStepComplete]);

  const handleAddSCN = useCallback(() => {
    if (predictions.scn) {
      setTubes(prev => ({
        ...prev,
        test2: {
          ...prev.test2,
          intensity: Math.min(prev.test2.intensity + 35, 95),
          color: calculateColor(Math.min(prev.test2.intensity + 35, 95)),
          additions: [...prev.test2.additions, 'SCN⁻']
        }
      }));
      setObservations(prev => [...prev, 'Tube 2: Color darkened (more blood red) after adding SCN⁻']);
      if (onStepComplete) onStepComplete('leChatelier-5');
    }
  }, [predictions.scn, onStepComplete]);

  const handlePredictNaOH = useCallback((prediction: string) => {
    setPredictions(prev => ({ ...prev, naoh: prediction }));
    if (onStepComplete) onStepComplete('leChatelier-6');
  }, [onStepComplete]);

  const handleAddNaOH = useCallback(() => {
    if (predictions.naoh) {
      setTubes(prev => ({
        ...prev,
        test3: {
          ...prev.test3,
          intensity: Math.max(prev.test3.intensity - 40, 10),
          color: calculateColor(Math.max(prev.test3.intensity - 40, 10)),
          additions: [...prev.test3.additions, 'NaOH']
        }
      }));
      setObservations(prev => [...prev, 'Tube 3: Color lightened (pale orange/yellow) after adding NaOH — Fe(OH)₃ precipitated']);
      if (onStepComplete) onStepComplete('leChatelier-7');
    }
  }, [predictions.naoh, onStepComplete]);

  const handleCompareAll = useCallback(() => {
    setComparisonComplete(true);
    if (onStepComplete) onStepComplete('leChatelier-8');
  }, [onStepComplete]);

  // Test tube component
  const TestTube = ({ id, label, state }: { id: string; label: string; state: TubeState }) => (
    <div className="test-tube-wrapper">
      <span className="tube-label">{label}</span>
      <svg viewBox="0 0 50 120" width={50} height={120}>
        <defs>
          <linearGradient id={`tube-glass-${id}`} x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="rgba(200,220,240,0.5)" />
            <stop offset="50%" stopColor="rgba(255,255,255,0.3)" />
            <stop offset="100%" stopColor="rgba(200,220,240,0.4)" />
          </linearGradient>
        </defs>

        {/* Tube */}
        <rect x="12" y="10" width="26" height="85" fill={`url(#tube-glass-${id})`} rx="13" />
        <ellipse cx="25" cy="95" rx="13" ry="6" fill="rgba(200,220,240,0.4)" />

        {/* Solution */}
        <rect x="14" y="30" width="22" height="62" fill={state.color} opacity="0.85" rx="11" />

        {/* Liquid surface meniscus */}
        <ellipse cx="25" cy="30" rx="11" ry="4" fill={state.color} opacity="0.7" />

        {/* Glass shine */}
        <line x1="16" y1="20" x2="16" y2="75" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" />

        {/* Precipitate indication for NaOH tube */}
        {state.additions.includes('NaOH') && (
          <ellipse cx="25" cy="88" rx="8" ry="4" fill="#8B4513" opacity="0.7" />
        )}
      </svg>
      <div className="tube-additions">
        {state.additions.map((add, i) => (
          <span key={i} className="addition-badge">{add}</span>
        ))}
      </div>
    </div>
  );

  // Prediction selector component
  const PredictionSelector = ({ testId, onSelect, current }: {
    testId: string;
    onSelect: (prediction: string) => void;
    current: string | null;
  }) => (
    <div className="prediction-selector">
      <span className="prediction-prompt">Predict equilibrium shift:</span>
      <div className="prediction-buttons">
        <button
          className={`pred-btn ${current === 'right' ? 'selected right' : ''}`}
          onClick={() => onSelect('right')}
        >
          <ArrowRight size={14} />
          Forward (more product)
        </button>
        <button
          className={`pred-btn ${current === 'left' ? 'selected left' : ''}`}
          onClick={() => onSelect('left')}
        >
          <ArrowLeft size={14} />
          Reverse (less product)
        </button>
        <button
          className={`pred-btn ${current === 'none' ? 'selected none' : ''}`}
          onClick={() => onSelect('none')}
        >
          <Minus size={14} />
          No change
        </button>
      </div>
    </div>
  );

  return (
    <div className="exp10-part-layout">
      {/* Steps Panel */}
      <div className="exp10-steps-panel">
        <div className="exp10-steps-header">
          <ListChecks size={16} />
          <h3>Le Chatelier&apos;s Principle</h3>
        </div>
        <div className="exp10-steps-list">
          {steps.map(s => {
            const done = stepDone[s.id - 1];
            const active = s.id === activeStep;
            return (
              <div key={s.id} className={`exp10-step-item ${active && !done ? 'active' : ''}`}>
                <div className={`exp10-step-number ${done ? 'done' : active ? 'current' : ''}`}>
                  {done ? <Check size={12} /> : s.id}
                </div>
                <div className="exp10-step-content">
                  <span className={`exp10-step-title ${active ? 'highlight' : ''}`}>{s.title}</span>
                  {active && !done && <span className="exp10-step-hint">{s.short}</span>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Lab Bench */}
      <div className="exp10-lab-bench">
        <div className="exp10-bench-header">
          <div className="exp10-bench-title"><FlaskConical size={16} /><h3>Lab Bench</h3></div>
          <span className="exp10-bench-progress">{completedCount}/{steps.length} Complete</span>
        </div>
        <div className="exp10-bench-area">
      <div className="le-chatelier-lab">
        {/* Equilibrium Equation */}
        <div className="equation-banner">
          <span className="equation-text">
            Fe<sup>3+</sup><span className="pale">(pale yellow)</span> + SCN<sup>−</sup><span className="colorless">(colorless)</span>
            <span className="equilibrium-arrows">
              <ArrowRight size={16} />
              <ArrowLeft size={16} />
            </span>
            FeSCN<sup>2+</sup><span className="blood-red">(blood red)</span>
          </span>
          <span className="keq">K<sub>eq</sub> = 890 at 25 °C</span>
        </div>

        {/* Test Tube Rack */}
        <div className="tube-rack">
          <div className="rack-base">
            <TestTube id="ref" label="Reference" state={tubes.reference} />
            <TestTube id="t1" label="Test 1" state={tubes.test1} />
            <TestTube id="t2" label="Test 2" state={tubes.test2} />
            <TestTube id="t3" label="Test 3" state={tubes.test3} />
          </div>
        </div>

        {/* Steps and Actions */}
        <div className="steps-area">
          {/* Step 1: Prepare Reference */}
          {!referencePrepped && (
            <div className="step-card">
              <div className="step-card-header">
                <h4>Step 1: Prepare Reference Solution</h4>
                {stepInfoContent[1] && (
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(1); }}
                  >
                    ?
                  </button>
                )}
              </div>
              <p>Mix 10 mL each of 0.01 M FeCl₃ and 0.01 M KSCN to establish equilibrium.</p>
              <button className="action-btn" onClick={handlePrepareReference}>
                <Droplets size={16} />
                Prepare Reference
              </button>
            </div>
          )}

          {/* Step 2-3: Predict and Add Fe3+ */}
          {referencePrepped && !tubes.test1.additions.length && (
            <div className="step-card">
              <div className="step-card-header">
                <h4>Step 2–3: Add Fe³⁺ to Test Tube 1</h4>
                {stepInfoContent[2] && (
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(2); }}
                  >
                    ?
                  </button>
                )}
              </div>
              <PredictionSelector
                testId="fe3"
                onSelect={handlePredictFe3}
                current={predictions.fe3}
              />
              {predictions.fe3 && (
                <button className="action-btn" onClick={handleAddFe3}>
                  <Droplets size={16} />
                  Add FeCl₃
                </button>
              )}
            </div>
          )}

          {/* Step 4-5: Predict and Add SCN- */}
          {tubes.test1.additions.length > 0 && !tubes.test2.additions.length && (
            <div className="step-card">
              <div className="step-card-header">
                <h4>Step 4–5: Add SCN⁻ to Test Tube 2</h4>
                {stepInfoContent[4] && (
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(4); }}
                  >
                    ?
                  </button>
                )}
              </div>
              <PredictionSelector
                testId="scn"
                onSelect={handlePredictSCN}
                current={predictions.scn}
              />
              {predictions.scn && (
                <button className="action-btn" onClick={handleAddSCN}>
                  <Droplets size={16} />
                  Add KSCN
                </button>
              )}
            </div>
          )}

          {/* Step 6-7: Predict and Add NaOH */}
          {tubes.test2.additions.length > 0 && !tubes.test3.additions.length && (
            <div className="step-card">
              <div className="step-card-header">
                <h4>Step 6–7: Add NaOH to Test Tube 3</h4>
                {stepInfoContent[6] && (
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(6); }}
                  >
                    ?
                  </button>
                )}
              </div>
              <p className="hint">NaOH will precipitate Fe³⁺ as Fe(OH)₃, removing it from solution.</p>
              <PredictionSelector
                testId="naoh"
                onSelect={handlePredictNaOH}
                current={predictions.naoh}
              />
              {predictions.naoh && (
                <button className="action-btn" onClick={handleAddNaOH}>
                  <Droplets size={16} />
                  Add NaOH
                </button>
              )}
            </div>
          )}

          {/* Step 8: Compare All */}
          {tubes.test3.additions.length > 0 && !comparisonComplete && (
            <div className="step-card">
              <div className="step-card-header">
                <h4>Step 8: Compare All Tubes</h4>
                {stepInfoContent[8] && (
                  <button
                    className="step-info-btn"
                    onClick={(e) => { e.stopPropagation(); setShowInfo(8); }}
                  >
                    ?
                  </button>
                )}
              </div>
              <p>Compare the colors of all test tubes to the reference.</p>
              <button className="action-btn success" onClick={handleCompareAll}>
                <Eye size={16} />
                Compare and Record
              </button>
            </div>
          )}
        </div>

        {/* Observations Panel */}
        {observations.length > 0 && (
          <div className="observations-panel">
            <h4>Observations</h4>
            <ul>
              {observations.map((obs, i) => (
                <li key={i}>{obs}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Completion Summary */}
        {comparisonComplete && (
          <div className="completion-summary">
            <Check size={24} />
            <div>
              <h4>Le Chatelier&apos;s Principle Demonstrated!</h4>
              <ul>
                <li><strong>Adding Fe³⁺:</strong> Equilibrium shifts right → darker red (more FeSCN²⁺)</li>
                <li><strong>Adding SCN⁻:</strong> Equilibrium shifts right → darker red (more FeSCN²⁺)</li>
                <li><strong>Removing Fe³⁺ (NaOH):</strong> Equilibrium shifts left → lighter color (less FeSCN²⁺)</li>
              </ul>
            </div>
          </div>
        )}

        <style>{`
          .exp10-part-layout { display: flex; gap: 16px; flex: 1; width: 100%; min-height: 0; padding: 16px; }
          .exp10-steps-panel { width: 240px; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: hidden; }
          .exp10-steps-header { background: linear-gradient(135deg, var(--primary), var(--primary-dark)); padding: 14px 16px; display: flex; align-items: center; gap: 10px; color: white; }
          .exp10-steps-header h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp10-steps-list { flex: 1; overflow-y: auto; padding: 12px; }
          .exp10-step-item { display: flex; align-items: flex-start; gap: 12px; padding: 8px 10px; border-radius: 8px; margin-bottom: 2px; }
          .exp10-step-item.active { background: rgba(255, 90, 54, 0.15); }
          .exp10-step-number { width: 24px; height: 24px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; background: var(--border); color: var(--text-muted); }
          .exp10-step-number.done { background: var(--secondary); color: white; }
          .exp10-step-number.current { background: var(--primary); color: white; }
          .exp10-step-content { flex: 1; min-width: 0; }
          .exp10-step-title { font-size: 12px; font-weight: 600; display: block; }
          .exp10-step-title.highlight { color: var(--primary-light); }
          .exp10-step-hint { font-size: 11px; color: var(--text-secondary); margin-top: 4px; display: block; }
          .exp10-lab-bench { flex: 1; background: var(--bg-surface); border-radius: 12px; border: 1px solid var(--border); display: flex; flex-direction: column; overflow: auto; }
          .exp10-bench-header { background: linear-gradient(135deg, var(--secondary), var(--secondary-dark)); padding: 12px 16px; display: flex; align-items: center; justify-content: space-between; color: white; }
          .exp10-bench-title { display: flex; align-items: center; gap: 10px; }
          .exp10-bench-title h3 { font-size: 14px; font-weight: 600; margin: 0; }
          .exp10-bench-progress { font-size: 12px; opacity: 0.9; }
          .exp10-bench-area { flex: 1; padding: 20px; overflow-y: auto; }

          .le-chatelier-lab {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 24px;
            width: 100%;
            max-width: 600px;
          }

          .equation-banner {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 8px;
            padding: 16px 24px;
            background: var(--bg-elevated);
            border-radius: 12px;
            width: 100%;
          }

          .equation-text {
            font-size: 16px;
            display: flex;
            align-items: center;
            gap: 8px;
            flex-wrap: wrap;
            justify-content: center;
          }

          .equilibrium-arrows {
            display: flex;
            flex-direction: column;
            color: var(--text-muted);
          }

          .pale { color: #DAA520; font-size: 10px; }
          .colorless { color: #888; font-size: 10px; }
          .blood-red { color: #8B0000; font-size: 10px; }

          .keq {
            font-size: 12px;
            color: var(--text-muted);
          }

          .tube-rack {
            padding: 20px;
            background: linear-gradient(180deg, #5C534A 0%, #3D352F 100%);
            border-radius: 12px;
          }

          .rack-base {
            display: flex;
            gap: 24px;
            padding: 12px 20px;
            background: #4A423B;
            border-radius: 8px;
          }

          .test-tube-wrapper {
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 4px;
          }

          .tube-label {
            font-size: 10px;
            color: #CCC;
            font-weight: 500;
          }

          .tube-additions {
            display: flex;
            gap: 2px;
            min-height: 20px;
          }

          .addition-badge {
            font-size: 8px;
            padding: 2px 4px;
            background: rgba(255,255,255,0.2);
            border-radius: 4px;
            color: #FFF;
          }

          .steps-area {
            width: 100%;
          }

          .step-card {
            background: var(--bg-elevated);
            padding: 16px 20px;
            border-radius: 12px;
            border: 2px solid var(--primary);
          }

          .step-card h4 {
            margin: 0 0 8px 0;
            font-size: 14px;
            color: var(--text-primary);
          }

          .step-card p {
            margin: 0 0 12px 0;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .step-card .hint {
            font-style: italic;
            color: var(--text-muted);
          }

          .prediction-selector {
            margin-bottom: 12px;
          }

          .prediction-prompt {
            font-size: 12px;
            color: var(--text-secondary);
            display: block;
            margin-bottom: 8px;
          }

          .prediction-buttons {
            display: flex;
            gap: 8px;
            flex-wrap: wrap;
          }

          .pred-btn {
            display: flex;
            align-items: center;
            gap: 4px;
            padding: 8px 12px;
            background: var(--bg-dark);
            border: 1px solid var(--border);
            border-radius: 6px;
            font-size: 11px;
            color: var(--text-secondary);
            cursor: pointer;
            transition: all 0.2s;
          }

          .pred-btn:hover {
            border-color: var(--primary);
          }

          .pred-btn.selected {
            color: white;
          }

          .pred-btn.selected.right {
            background: #00A86B;
            border-color: #00A86B;
          }

          .pred-btn.selected.left {
            background: #FF5A36;
            border-color: #FF5A36;
          }

          .pred-btn.selected.none {
            background: #888;
            border-color: #888;
          }

          .action-btn {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 10px 16px;
            background: var(--primary);
            color: white;
            border: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s;
          }

          .action-btn:hover {
            background: var(--primary-dark);
          }

          .action-btn.success {
            background: #00A86B;
          }

          .observations-panel {
            width: 100%;
            background: var(--bg-elevated);
            padding: 16px;
            border-radius: 12px;
          }

          .observations-panel h4 {
            margin: 0 0 8px 0;
            font-size: 13px;
            color: var(--text-secondary);
          }

          .observations-panel ul {
            margin: 0;
            padding-left: 20px;
            font-size: 12px;
            color: var(--text-primary);
          }

          .observations-panel li {
            margin-bottom: 4px;
          }

          .completion-summary {
            display: flex;
            gap: 16px;
            padding: 20px;
            background: rgba(0, 168, 107, 0.1);
            border: 1px solid #00A86B;
            border-radius: 12px;
            color: #00A86B;
            width: 100%;
          }

          .completion-summary h4 {
            margin: 0 0 8px 0;
            font-size: 14px;
          }

          .completion-summary ul {
            margin: 0;
            padding-left: 20px;
            font-size: 12px;
            color: var(--text-secondary);
          }

          .completion-summary li {
            margin-bottom: 4px;
          }

          .completion-summary strong {
            color: var(--text-primary);
          }

          .step-card-header {
            display: flex;
            align-items: center;
            justify-content: space-between;
            gap: 8px;
            margin-bottom: 8px;
          }

          .step-card-header h4 {
            margin: 0;
          }

          .step-info-btn {
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: var(--accent);
            color: var(--bg-dark);
            font-size: 11px;
            font-weight: 700;
            border: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: transform 0.15s;
          }

          .step-info-btn:hover {
            transform: scale(1.1);
          }
        `}</style>

        {/* Info Box */}
        {showInfo && stepInfoContent[showInfo] && (
          <InfoBox
            step={showInfo}
            experimentId="exp10"
            stepInfo={stepInfoContent[showInfo]}
            onClose={() => setShowInfo(null)}
          />
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
